import { SearchAutocomplete, Stethoscope } from "@ews-admin/global-design-system";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useFacilityUser from "../../../hooks/useFacilityUser";
import { searchApi } from "../../../services/search.api";
import { Doctor, Doctor as DoctorType } from "../../../types/doctor.definition";
import { SearchRequest } from "../../../types/search.definition";

interface DoctorSearchAutocompleteProps {
  onSelect: (doctorId: string, doctor?: DoctorType) => void;
  selectedDoctorId: string;
  disabled?: boolean;
}

export const DoctorSearchAutocomplete: React.FC<
  DoctorSearchAutocompleteProps
> = ({ onSelect, selectedDoctorId, disabled = false }) => {
  const { t } = useTranslation();
  // Use memoized callbacks to prevent unnecessary re-renders
  const facilityUser = useFacilityUser();
  const facilityId = Number(facilityUser.healthcareFacility?.id);
  const searchDoctors = useCallback(async (keyword: string) => {
    const payload: SearchRequest = {
      paginationSettings: {
        limit: 10,
        offset: 0,
      },
      sortBy: {
        property: "user.firstName",
        order: "asc",
      },
      query: {
        keyword,
      },
    };

    const response = await searchApi.searchDoctorsInFacility(
      payload,
      facilityId
    );
    return response.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDoctorDisplayValue = useCallback((doctor: Doctor) => {
    return `Dr. ${doctor.firstName} ${doctor.lastName}`;
  }, []);

  const getDoctorSecondaryText = useCallback((doctor: Doctor) => {
    const parts: string[] = [];
    if (doctor.email) parts.push(doctor.email);
    return parts.join(" • ");
  }, []);

  // Create a stable wrapper for onSelect to prevent cascading effects
  const handleSelect = useCallback(
    async (id: string, doctor?: Doctor) => {
      // Only call onSelect if the ID actually changed
      if (id !== selectedDoctorId) {
        try {
          onSelect(id, doctor);
        } catch (error) {
          console.error("Error fetching doctor data:", error);
          // Fallback to just the ID if fetching fails
          onSelect(id);
        }
      }
    },
    [onSelect, selectedDoctorId]
  );

  return (
    <SearchAutocomplete<Doctor>
      onSelect={handleSelect}
      selectedId={selectedDoctorId}
      searchFunction={searchDoctors}
      getDisplayValue={getDoctorDisplayValue}
      getSecondaryText={getDoctorSecondaryText}
      placeholder={t("appointment.manage.autocomplete.searchDoctors")}
      noResultsText={t("appointment.manage.autocomplete.noResults")}
      icon={Stethoscope}
      disabled={disabled}
      minSearchLength={2}
          getMinCharactersHint={(min) => t("common.searchMinCharacters", { count: min })}
      debounceTime={300}
    />
  );
};
